import type { EstadoOt } from '../schemas/ticket.js';

/**
 * Valid state transitions for the ticket state machine.
 * This is the SINGLE SOURCE OF TRUTH for allowed transitions.
 * SACRED - NEVER BYPASS. All state changes go through transitionTicket().
 */
export const VALID_TRANSITIONS = {
  PROGRAMADO: ['INICIADO', 'NO_EJECUTADO'],
  INICIADO: ['EJECUTADO', 'NO_EJECUTADO', 'PROGRAMADO'],
  NO_EJECUTADO: ['PROGRAMADO', 'REPROGRAMADO'],
  EJECUTADO: ['APROBADO', 'RECHAZADO', 'INICIADO'],
  APROBADO: [],
  RECHAZADO: ['INICIADO'],
  REPROGRAMADO: ['PROGRAMADO'],
} as const satisfies Record<EstadoOt, readonly EstadoOt[]>;

/**
 * Terminal states - derived from VALID_TRANSITIONS (no further transitions allowed).
 */
export const TERMINAL_STATES = (
  Object.entries(VALID_TRANSITIONS) as [EstadoOt, readonly EstadoOt[]][]
)
  .filter(([, targets]) => targets.length === 0)
  .map(([state]) => state);

/**
 * Subset of transitions an OPERADOR is allowed to drive through
 * PATCH /tickets/:id/transition. The dedicated /start and /complete endpoints
 * exist for the happy path; this set is the RBAC gate when an OPERADOR uses
 * the generic transition endpoint. Frontend reads it to filter validTargets,
 * backend reads it to reject 403s. Single source prevents drift.
 */
export const ALLOWED_OPERADOR_TRANSITIONS: ReadonlySet<string> = new Set([
  'PROGRAMADO->INICIADO',
  'INICIADO->EJECUTADO',
]);

/**
 * All possible ticket states - derived from VALID_TRANSITIONS.
 */
export const ALL_STATES = Object.keys(VALID_TRANSITIONS) as EstadoOt[];

/**
 * Estados "ejecutados" — el trabajo está hecho y el ticket puede tener PDF:
 * EJECUTADO (ejecutado, pendiente de firma) + APROBADO (firmado). Es el universo
 * canónico de los reportes de tickets ejecutados; espeja el legacy
 * `estado_ot_id IN (4,5)`. SINGLE SOURCE OF TRUTH — la copia de
 * `apps/api/src/modules/reportes/tooltime-universe.ts` re-exporta DESDE aquí y
 * el frontend (report-tickets) la usa para filtrar la lista por defecto, para
 * que el criterio nunca se duplique ni diverja entre capas.
 */
export const REPORTABLE_ESTADOS = ['EJECUTADO', 'APROBADO'] as const satisfies readonly EstadoOt[];

/**
 * Badge color mapping for ticket states (frontend usage).
 * Use STATE_LABELS for display names.
 */
export const STATE_COLORS: Record<EstadoOt, { bg: string; text: string }> = {
  PROGRAMADO: { bg: '#007AFF', text: '#FFFFFF' },
  INICIADO: { bg: '#FF9F0A', text: '#FFFFFF' },
  NO_EJECUTADO: { bg: '#8E8E93', text: '#FFFFFF' },
  EJECUTADO: { bg: '#30D158', text: '#FFFFFF' },
  APROBADO: { bg: '#00B4D8', text: '#FFFFFF' },
  RECHAZADO: { bg: '#FF453A', text: '#FFFFFF' },
  REPROGRAMADO: { bg: '#AF52DE', text: '#FFFFFF' },
};

/**
 * Spanish labels for ticket states (UI display).
 */
export const STATE_LABELS: Record<EstadoOt, string> = {
  PROGRAMADO: 'Programado',
  INICIADO: 'Iniciado',
  NO_EJECUTADO: 'No Ejecutado',
  EJECUTADO: 'Ejecutado',
  APROBADO: 'Aprobado',
  RECHAZADO: 'Rechazado',
  REPROGRAMADO: 'Reprogramado',
};

export const STATE_HINTS: Record<EstadoOt, string> = {
  PROGRAMADO: 'Listo para ejecutar. Puede iniciar, marcar como no ejecutado o eliminar.',
  INICIADO: 'En ejecucion por el operador. Puede completar o marcar como no ejecutado.',
  NO_EJECUTADO: 'Cerrado sin ejecutar. Puede reabrir para reprogramar.',
  EJECUTADO: 'Completado, pendiente de aprobacion por coordinador.',
  APROBADO: 'Aprobado y firmado. No requiere mas acciones.',
  RECHAZADO: 'Rechazado por coordinador. Puede editar y reenviar.',
  REPROGRAMADO: 'Pendiente de nueva fecha. Devuelva a Programado para continuar.',
};
